"use client";

import * as React from "react";
import Image from "next/image";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import type { ProjectImage } from "@/constants/projects";

type ProjectGalleryProps = {
  images: ProjectImage[];
};

// Main image + arrows + thumbnails. With a single image it renders just that image.
export default function ProjectGallery({ images }: ProjectGalleryProps) {
  const [index, setIndex] = React.useState(0);
  const count = images.length;
  const multiple = count > 1;

  const go = React.useCallback(
    (delta: number) => setIndex((i) => (i + delta + count) % count),
    [count]
  );

  React.useEffect(() => {
    if (!multiple) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, multiple]);

  return (
    <div className="border-b border-white/[0.06] bg-black">
      <div className="group/gallery relative">
        <Image
          key={images[index].src}
          src={images[index].src}
          alt={images[index].alt}
          width={1600}
          height={900}
          sizes="(min-width: 1024px) 80vw, 92vw"
          className="mx-auto h-auto max-h-[34vh] w-full object-contain animate-in fade-in duration-300 sm:max-h-[46vh]"
          priority
        />

        {multiple && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 flex h-9 w-9 sm:left-3 sm:h-10 sm:w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/70 text-white backdrop-blur transition-colors hover:bg-black"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-2 top-1/2 flex h-9 w-9 sm:right-3 sm:h-10 sm:w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/70 text-white backdrop-blur transition-colors hover:bg-black"
            >
              <IconChevronRight size={20} />
            </button>
            <span className="absolute bottom-3 right-3 rounded-full border border-white/10 bg-black/70 px-2.5 py-1 text-[12px] font-semibold text-white backdrop-blur">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>

      {multiple && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-4 pt-1 sm:px-6 md:px-8">
          {images.map(({ src }, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show image ${i + 1}`}
              aria-current={i === index}
              className={`relative h-12 w-20 shrink-0 sm:h-14 sm:w-24 overflow-hidden rounded-[8px] border-2 transition-all ${
                i === index ? "border-[#48AEEF]" : "border-transparent opacity-50 hover:opacity-100"
              }`}
            >
              <Image src={src} alt="" fill sizes="96px" className="object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
